import type { WebContext } from "@reboot-dev/reboot-web";
import { List } from "immutable";

import { type DocumentMetadata, DocumentMetadataSchema } from "@moment/api-collab/api-types";
import { type Store as DifferentialStore } from "@moment/api-collab/differential";
import {
	type Action,
	hydrateAction,
	rawActionSchema,
} from "@moment/api-collab/differential/QueryEvaluator/Action";
import { Logger } from "@moment/logging";
import { type Store as RebootStore, type TableChangesResponse } from "@moment/reboot-differential";

const logger = new Logger("metadata-utils");

/**
 * Creates a sendToCollab callback that writes metadata to the store.
 */
export function makeMetadataSendToCollabCommand(
	store: DifferentialStore<{ metadata: DocumentMetadata }>
): (metadata: DocumentMetadata) => Promise<void> {
	return async (metadata: DocumentMetadata) => {
		await store.delete("metadata", "metadata");
		await store.insert("metadata", "metadata", metadata);
	};
}

/**
 * Parse TableChangesResponse into a list of Actions for reconciliation.
 */
function parseActions(value: TableChangesResponse): List<Action> {
	return List(value.changesJson).map((protoc) => {
		const parsed = rawActionSchema.parse(protoc.toJson());
		return hydrateAction(parsed);
	});
}

/**
 * Get the last epoch from received actions for version tracking.
 */
function getLastReceivedEpoch(receivedActions: List<Action>): number | undefined {
	const lastAction = receivedActions.last();
	if (!lastAction) {
		return undefined;
	}
	return lastAction.type === "queryRegistration"
		? lastAction.desiredStartEpoch
		: lastAction.desiredApplyEpoch;
}

/**
 * Get the latest metadata from the store without creating a subscription.
 */
export function getLatestMetadata(
	store: DifferentialStore<{ metadata: DocumentMetadata }>
): DocumentMetadata | undefined {
	const rawMetadata = store.latestState.tables.get("metadata")?.toArray()[0]?.tuple.getValue();

	if (!rawMetadata) {
		return undefined;
	}

	const parsedMetadata = DocumentMetadataSchema.safeParse(rawMetadata);
	if (!parsedMetadata.success) {
		logger.warn("Failed to parse metadata from differential store", {
			issues: parsedMetadata.error.issues,
		});
		return undefined;
	}

	return parsedMetadata.data;
}

/**
 * Creates a remoteChanges async generator that subscribes to Reboot tableChanges
 * and reconciles actions into the DifferentialStore, then yields metadata updates.
 *
 * This follows the same pattern as makeMdPageRemoteChangesCommand for editor state.
 */
export function makeMetadataRemoteChangesCommand(
	rebootStoreRef: RebootStore.WeakReference,
	context: WebContext,
	initialVersion: number,
	store: DifferentialStore<{ metadata: DocumentMetadata }>
): (ops: { signal: AbortSignal }) => AsyncIterable<DocumentMetadata> {
	return async function* remoteChanges(ops: { signal: AbortSignal }) {
		const signal = ops.signal;

		let fromVersion = initialVersion - 1;
		const [stream, update] = await rebootStoreRef
			.reactively()
			.tableChanges(context, { fromVersion }, { signal });

		while (!signal.aborted) {
			const result = await stream.next();
			if (result.done || !result.value) {
				logger.info("Remote tableChanges stream ended");
				break;
			}

			logger.debug("Received tableChanges from Reboot", { fromVersion });

			const receivedActions = parseActions(result.value);
			await store.reconcileActions(receivedActions);

			// Get current metadata after reconciliation and yield it
			const metadata = getLatestMetadata(store);
			if (metadata) {
				yield metadata;
			}

			// IF there is an action (there may not have been), AND there was a new version, update
			// the `fromVersion`.
			const lastHandledEpoch = getLastReceivedEpoch(receivedActions);
			const atLeastOneVersionWasHandled = lastHandledEpoch !== undefined;
			if (atLeastOneVersionWasHandled && lastHandledEpoch !== fromVersion) {
				fromVersion = lastHandledEpoch;

				// Update the stream to continue from the latest version
				update({ fromVersion });
			}
		}
	};
}
